/**
 * diagnostics.ts — Reporte de salud y verificación de corrupción de la capa
 * de persistencia. Herramienta de soporte técnico en campo.
 *
 * QUÉ RESUELVE:
 *  Cuando un cliente dice "desaparecieron las ventas", este módulo produce un
 *  reporte único con TODO el estado del almacenamiento (WAL, Mirror IDB,
 *  métricas, consistencia) para diagnosticar sin adivinar.
 *
 * QUÉ EXPORTA:
 *  - getHealthReport(): Promise<HealthReport> — estado completo de persistencia.
 *  - verifyMirrorConsistency(): Promise<ConsistencyResult> — compara localStorage
 *    con el espejo IndexedDB y detecta divergencias (corrupción/desincronización).
 *  - printHealthReport(): imprime el reporte en consola (soporte).
 *  Ambos se exponen en `window.__altenotHealth` para inspección en campo.
 *
 * QUÉ NO HACE:
 *  - No modifica datos (solo lectura + reporte). La reparación la hacen
 *    integrityCheck / reconstructCajaFromAuditoria / recoverStore.
 *  - No importa React ni UI.
 */

import { logger } from "@/lib/logger";
import { readJournal, JOURNAL_KEY } from "./writeAheadLog";
import { getActiveBackend } from "./storageBackend";
import { persistenceMetrics } from "./persistenceMetrics";
import { idbGetAll } from "./idbMirrorBackend";
import { parsePersistedValue } from "@/mocks/helpers/storage";

// ── Claves de negocio a vigilar (mismo criterio que restauración) ────────────

const BUSINESS_PREFIXES = [
  "caja_",
  "auditoria_",
  "inventory_",
  "personal_",
  "tienda_",
  "creditos_",
  "payroll_",
  "higiene_",
  "businessConfig",
];

function isBusinessKey(key: string): boolean {
  return BUSINESS_PREFIXES.some((p) => key.startsWith(p));
}

// ── Verificación de consistencia LS ↔ IDB ────────────────────────────────────

export interface ConsistencyDivergence {
  key: string;
  reason:
    | "solo_en_localStorage"
    | "solo_en_idb"
    | "valor_distinto"
    | "longitud_distinta";
  lsLength?: number;
  idbLength?: number;
}

export interface ConsistencyResult {
  ok: boolean;
  checkedKeys: number;
  divergences: ConsistencyDivergence[];
  checkedAt: string;
}

/**
 * Compara el estado de las claves de negocio en localStorage vs el espejo IDB.
 * Detecta corrupción o desincronización. No repara: solo informa.
 *
 * INTERPRETACIÓN:
 *  - "solo_en_idb": localStorage perdió la clave (kill de APK) → restore la trae.
 *  - "solo_en_localStorage": IDB aún no espejó (mirror async pendiente) → normal
 *    si es reciente; sospechoso si persiste.
 *  - "valor_distinto"/"longitud_distinta": posible corrupción → requiere revisión.
 */
export async function verifyMirrorConsistency(): Promise<ConsistencyResult> {
  const divergences: ConsistencyDivergence[] = [];
  const result: ConsistencyResult = {
    ok: true,
    checkedKeys: 0,
    divergences,
    checkedAt: new Date().toISOString(),
  };

  try {
    const idbEntries = await idbGetAll();

    // Unión de claves de negocio en ambos lados
    const lsKeys = Object.keys(localStorage).filter(isBusinessKey);
    const idbKeys = [...idbEntries.keys()].filter(isBusinessKey);
    const allKeys = new Set<string>([...lsKeys, ...idbKeys]);
    result.checkedKeys = allKeys.size;

    for (const key of allKeys) {
      const lsVal = localStorage.getItem(key);
      const idbVal = idbEntries.get(key) ?? null;

      if (lsVal !== null && idbVal === null) {
        divergences.push({ key, reason: "solo_en_localStorage" });
      } else if (lsVal === null && idbVal !== null) {
        divergences.push({ key, reason: "solo_en_idb" });
      } else if (lsVal !== null && idbVal !== null) {
        // Normalizar ambas representaciones: localStorage puede estar comprimido
        // (lz-string) mientras que el espejo IDB guarda el JSON plano. Se comparan
        // los valores parseados, no los strings crudos, para no reportar
        // divergencias falsas por compresión.
        const normalized = (raw: string): string => {
          try {
            return JSON.stringify(parsePersistedValue(raw));
          } catch {
            return raw;
          }
        };
        const lsNorm = normalized(lsVal);
        const idbNorm = normalized(idbVal);
        if (lsNorm !== idbNorm) {
          divergences.push({
            key,
            reason:
              lsNorm.length !== idbNorm.length
                ? "longitud_distinta"
                : "valor_distinto",
            lsLength: lsVal.length,
            idbLength: idbVal.length,
          });
        }
      }
    }

    // Solo marcamos NO-ok ante divergencias sospechosas de corrupción
    // (valores distintos). "solo_en_idb" y "solo_en_localStorage" pueden ser
    // transitorios (mirror async / restore pendiente).
    result.ok = !divergences.some(
      (d) => d.reason === "valor_distinto" || d.reason === "longitud_distinta",
    );
  } catch (err) {
    persistenceMetrics.recordError(
      "verifyMirrorConsistency",
      err instanceof Error ? err.message : String(err),
    );
    result.ok = false;
  }

  return result;
}

// ── Health Report completo ────────────────────────────────────────────────────

export interface HealthReport {
  timestamp: string;
  wal: {
    pendingJournal: boolean;
    pendingWrites: number;
  };
  mirror: {
    idbAvailable: boolean;
    idbKeyCount: number;
    lsBusinessKeyCount: number;
  };
  consistency: ConsistencyResult;
  metrics: {
    summary: Record<string, number>;
    timing: ReturnType<typeof persistenceMetrics.getTimingSummary>;
  };
  storageEstimate?: {
    usageMB: number;
    quotaMB: number;
    percentUsed: number;
  };
}

/**
 * Produce un reporte técnico completo del estado de la capa de persistencia.
 * Pensado para soporte: un solo objeto que responde "¿está sano el almacenamiento?".
 */
export async function getHealthReport(): Promise<HealthReport> {
  const backend = getActiveBackend();

  // WAL
  const journal = readJournal(backend);
  const pendingWrites =
    journal && Array.isArray(journal.writes) ? journal.writes.length : 0;

  // Mirror + consistencia
  const consistency = await verifyMirrorConsistency();
  let idbKeyCount = 0;
  let idbAvailable = true;
  try {
    const idbEntries = await idbGetAll();
    idbKeyCount = idbEntries.size;
  } catch {
    idbAvailable = false;
  }

  const lsBusinessKeyCount =
    Object.keys(localStorage).filter(isBusinessKey).length;

  // Estimación de cuota (si el navegador lo soporta)
  let storageEstimate: HealthReport["storageEstimate"];
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const est = await navigator.storage.estimate();
      const usage = est.usage ?? 0;
      const quota = est.quota ?? 0;
      storageEstimate = {
        usageMB: Math.round((usage / 1024 / 1024) * 100) / 100,
        quotaMB: Math.round((quota / 1024 / 1024) * 100) / 100,
        percentUsed: quota > 0 ? Math.round((usage / quota) * 10000) / 100 : 0,
      };
    }
  } catch {
    /* no soportado: omitir */
  }

  return {
    timestamp: new Date().toISOString(),
    wal: {
      pendingJournal: pendingWrites > 0,
      pendingWrites,
    },
    mirror: {
      idbAvailable,
      idbKeyCount,
      lsBusinessKeyCount,
    },
    consistency,
    metrics: {
      summary: persistenceMetrics.getReport().summary,
      timing: persistenceMetrics.getTimingSummary(),
    },
    storageEstimate,
  };
}

/** Imprime el reporte de salud en consola (soporte técnico en campo). */
export async function printHealthReport(): Promise<HealthReport> {
  const report = await getHealthReport();
  logger.group("[Altenot Health Report]");
  logger.log("WAL:", report.wal);
  logger.log("Mirror IDB:", report.mirror);
  logger.log(
    `Consistencia: ${report.consistency.ok ? "OK" : "DIVERGENCIAS"} ` +
      `(${report.consistency.divergences.length} divergencias sobre ` +
      `${report.consistency.checkedKeys} claves)`,
  );
  if (report.consistency.divergences.length > 0) {
    logger.table(report.consistency.divergences);
  }
  logger.log("Métricas:", report.metrics.summary);
  logger.log("Rendimiento:", report.metrics.timing);
  if (report.storageEstimate) {
    logger.log("Almacenamiento:", report.storageEstimate);
  }
  logger.groupEnd();
  return report;
}

// Exponer en window para soporte en campo (DevTools / Eruda).
if (typeof window !== "undefined") {
  (
    window as Window & {
      __altenotHealth?: {
        getHealthReport: typeof getHealthReport;
        printHealthReport: typeof printHealthReport;
        verifyMirrorConsistency: typeof verifyMirrorConsistency;
      };
    }
  ).__altenotHealth = {
    getHealthReport,
    printHealthReport,
    verifyMirrorConsistency,
  };
}

// Referencia para evitar tree-shaking del import de JOURNAL_KEY en algunos bundlers.
export { JOURNAL_KEY };
